"use client"

import { useState } from "react";
import { Coffee, Mic, Users, Utensils, Ship, Anchor, Wine } from "lucide-react";

const Schedule = () => {
  const [activeDay, setActiveDay] = useState("day1");

  // Map emoji icons to Lucide icons with professional styling
  const getIcon = (iconType) => {
    switch(iconType) {
      case "☕": return <Coffee className="h-5 w-5 stroke-[1.5]" />;
      case "🎤": return <Mic className="h-5 w-5 stroke-[1.5]" />;
      case "👥": return <Users className="h-5 w-5 stroke-[1.5]" />;
      case "🍽️": return <Utensils className="h-5 w-5 stroke-[1.5]" />;
      case "🍹": return <Wine className="h-5 w-5 stroke-[1.5]" />;
      default: return <Ship className="h-5 w-5 stroke-[1.5]" />;
    }
  };

  const scheduleData = {
    day1: [
      { 
        time: "09:00 AM – 09:30 AM", 
        title: "Opening Ceremony: Framing the Theme and the African Dream",
        description: "The Africa Maritime Conference officially opens with a welcome address by the Master of Ceremonies, followed by an opening keynote from H.E Dr. Alemu Sime, Minister of Transport and Logistics. This inaugural session sets the tone for the conference, introducing its core themes and spotlighting Africa's growing role in shaping the future of global maritime affairs.",
        icon: "🎤"
      },
      { 
        time: "09:30 AM – 11:00 AM", 
        title: "Keynote Address: The Maritime World Today and Africa's Role in It",
        description: "Presented by Mr. Frans Joubert, CEO of YCF Manning Ltd, this keynote will provide a comprehensive overview of the current global maritime landscape. The presentation will cover the structure of the maritime industry, the leading nations and stakeholders shaping global trade, and the current state of global crewing and supply chains.",
        icon: "🎤"
      },
      { 
        time: "11:00 AM – 11:30 AM", 
        title: "Coffee Break & Networking",
        description: "A mid-morning intermission offering delegates the opportunity to connect, reflect on the keynote presentation, and prepare for the technical and thematic sessions ahead.",
        icon: "☕"
      },
      { 
        time: "11:30 AM – 12:00 PM", 
        title: "Keynote Address: Women in Maritime – Access, Technology, and Leadership",
        description: "H.E. Ambassador Nancy Karigithu, EBS, Special Envoy for Maritime and Blue Economy in the Executive Office of the President of Kenya, delivers a powerful address on behalf of the Association of Women in the Maritime Sector in Eastern and Southern Africa (WOMESA).",
        icon: "🎤"
      },
      { 
        time: "12:00 PM – 1:00 PM", 
        title: "Flag State Leadership in Crew Development and Maritime Human Capital Growth",
        description: "Mr. Alfonso Castillero, Chief Executive Officer of the Liberian International Ship and Corporate Registry (LISCR), will deliver a high-level address on the strategic importance of top-tier flag states in shaping global maritime standards.",
        icon: "👥"
      },
      { 
        time: "1:00 PM – 2:30 PM", 
        title: "Lunch Break",
        description: "A relaxed and elegant networking lunch offering delegates the opportunity to continue their conversations in a more informal setting.",
        icon: "🍽️"
      },
      { 
        time: "2:30 PM – 5:00 PM", 
        title: "Sideroom Sessions",
        description: "• Closed-Door Roundtable: Strengthening Africa's Maritime Academies\n• Closed-Door Roundtable: African Maritime Authorities\n• Closed-Door Roundtable: Shipping Registries and Flags of Convenience Across Africa\n• Meeting Rooms Available for private consultations",
        icon: "👥"
      },
      { 
        time: "7:00 PM – 9:00 PM", 
        title: "Evening Reception: High-Level Networking & Visionary Presentations",
        description: "An exclusive gathering of ministers, industry leaders, and global partners for an evening of high-level exchange, presentations and meaningful collaboration—advancing Africa's maritime agenda through strategic connections and shared vision.",
        icon: "🍽️"
      }
    ],
    day2: [
      { 
        time: "09:00 AM – 09:30 AM", 
        title: "Morning Opening: Reflections and Preview",
        description: "The day begins with a brief recap of Day One highlights and introducing the focus for Day Two — centering on the global shipping industry and the critical role of maritime education in Africa's growth.",
        icon: "🎤"
      },
      { 
        time: "09:30 AM – 11:00 AM", 
        title: "Industry Engagement Session I: Global Shipping Perspectives",
        description: "This session features leading international shipowners, operators, and management firms discussing trends in global crewing, maritime operations, and Africa's role in the global shipping workforce. Companies participating in this session include global names such as Columbia Shipmanagement (CSM), Seaspan, and others.",
        icon: "👥"
      },
      { 
        time: "11:00 AM – 11:30 AM", 
        title: "Coffee Break",
        icon: "☕"
      },
      { 
        time: "11:30 AM – 12:30 PM", 
        title: "Industry Engagement Session II: Global Shipping Perspectives",
        description: "Continuing the discussion from earlier in the day, this second segment welcomes additional shipping industry voices to share insights on talent development, technical compliance, and building long-term crew pipelines.",
        icon: "👥"
      },
      { 
        time: "1:00 PM – 2:00 PM", 
        title: "Lunch Break",
        icon: "🍽️"
      },
      { 
        time: "2:00 PM – 2:30 PM", 
        title: "Regional Education Spotlight: Maritime Academies of Southern Africa",
        description: "This session features representatives from leading maritime training institutions in Southern Africa, offering regional perspectives on maritime education and institutional engagement.",
        icon: "👥"
      },
      { 
        time: "2:30 PM – 3:00 PM", 
        title: "Regional Education Spotlight: Maritime Academies of West Africa",
        description: "Maritime academies from West Africa contribute to the continental conversation on education, training, and the role of academic institutions in supporting Africa's maritime vision.",
        icon: "👥"
      },
      { 
        time: "3:00 PM – 3:30 PM", 
        title: "Coffee Break",
        icon: "☕"
      },
      { 
        time: "3:30 PM – 4:00 PM", 
        title: "Regional Education Session: Maritime Academies of North Africa",
        description: "Institutions from North Africa join the program to share institutional experiences and explore areas of alignment with broader continental maritime goals.",
        icon: "👥"
      },
      { 
        time: "4:00 PM – 4:30 PM", 
        title: "Regional Education Session: Maritime Academies of East Africa",
        description: "East African academies take part in this session, reflecting on their contributions to regional training efforts and capacity development within the maritime sector.",
        icon: "👥"
      },
      { 
        time: "4:30 PM – 5:00 PM", 
        title: "National Focus: Maritime Education in Ethiopia",
        description: "This closing session of the day highlights Ethiopia's growing investments in maritime training and the country's ambition to position itself as a regional hub for maritime education.",
        icon: "👥"
      },
      { 
        time: "6:00 PM – 8:00 PM", 
        title: "Industry Networking Cocktail",
        description: "An elegant evening reception bringing together shipping executives, educators, and policymakers for informal networking, idea exchange, and cross-sector connection in a relaxed setting.",
        icon: "🍹"
      }
    ],
    day3: [
      { 
        time: "09:00 AM – 09:30 AM", 
        title: "Industry Insight: EMA Marine GmbH",
        description: "EMA Marine GmbH opens the day with a focused session on the realities of maritime talent sourcing across Africa. Drawing from direct experience in crewing and placement, this session will explore the opportunities and challenges facing manning service providers operating across the continent.",
        icon: "👥"
      },
      { 
        time: "09:30 AM – 10:15 AM", 
        title: "Diplomatic Panel Discussions & Presentations: Global Perspectives on Maritime Development in Africa",
        description: "This session features a panel of distinguished members of the diplomatic corps. Representatives will reflect on their respective countries' maritime engagements in Africa, the importance of partnerships in training and talent development, and pathways to strengthen bilateral and multilateral maritime cooperation.",
        icon: "👥"
      },
      { 
        time: "10:15 AM – 11:00 AM", 
        title: "Panel Discussion & Presentations: Maritime Authorities and the Future of Policy, Regulation, and Investment",
        description: "Senior representatives from national and regional maritime authorities will engage in a moderated dialogue on the policy direction, structural reforms, and regulatory investments required to drive Africa's maritime sector forward.",
        icon: "👥"
      },
      { 
        time: "11:00 AM – 11:30 AM", 
        title: "Coffee Break & Networking",
        icon: "☕"
      },
      { 
        time: "11:30 AM – 1:00 PM", 
        title: "The Great Debate: Can Africa Become a Leading Global Supplier of Seafarers in the Next Decade?",
        description: "This dynamic, cross-sectoral debate invites experts, regulators, training institutions, and private sector voices to weigh in on Africa's ability to meet global demand for seafarers and maritime professionals.",
        icon: "👥"
      },
      { 
        time: "1:00 PM – 1:15 PM", 
        title: "Conference Closing & Announcement of AMC 2026 Theme",
        description: "The Africa Maritime Conference 2025 concludes with final remarks and acknowledgments from the organizers, followed by the official unveiling of the theme for AMC 2026.",
        icon: "🎤"
      },
      { 
        time: "1:15 PM – 3:00 PM", 
        title: "Networking Lunch and Departure",
        description: "The final networking lunch offers delegates a last opportunity to engage, reflect, and solidify connections established over the course of the conference.",
        icon: "🍽️"
      }
    ]
  };

  return (
    <section id="schedule" className="relative py-20 overflow-hidden" style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)' }}>
      {/* Pattern Overlay */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'url(/patterns/AMC_Blue Pattern-8.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.1,
        }}
      />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-primary-navy mb-4">Last Year's Agenda</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Review the agenda from our previous conference featuring insightful discussions, networking opportunities, and collaborative sessions.
          </p>
        </div>

        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-4 mb-8 overflow-x-auto pb-4">
            <button
              onClick={() => setActiveDay('day1')}
              className={`px-6 py-3 rounded-full font-medium transition-colors whitespace-nowrap ${
                activeDay === 'day1'
                  ? 'bg-primary-navy text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Day 1 - Tuesday, Oct 14
            </button>
            <button
              onClick={() => setActiveDay('day2')}
              className={`px-6 py-3 rounded-full font-medium transition-colors whitespace-nowrap ${
                activeDay === 'day2'
                  ? 'bg-primary-navy text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Day 2 - Wednesday, Oct 15
            </button>
            <button
              onClick={() => setActiveDay('day3')}
              className={`px-6 py-3 rounded-full font-medium transition-colors whitespace-nowrap ${
                activeDay === 'day3'
                  ? 'bg-primary-navy text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Day 3 - Thursday, Oct 16
            </button>
          </div>

          <div className="space-y-6">
            {scheduleData[activeDay].map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg border border-gray-100"
              >
                <div className="p-6 md:flex md:items-start">
                  <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6">
                    <div className="flex items-center justify-center h-16 w-16 rounded-full bg-primary-navy/10 text-primary-navy">
                      {getIcon(item.icon)}
                    </div>
                    <span className="block mt-2 text-sm font-medium text-gray-500 text-center">
                      {item.time}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-primary-navy mb-2">
                      {item.title}
                    </h3>
                    {item.description && (
                      <div className="text-gray-600 space-y-2">
                        {item.description.split('\n').map((paragraph, i) => (
                          <p key={i} className={paragraph.startsWith('•') ? 'ml-4' : ''}>
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer note */}
          <div className="bg-gray-50 p-3 sm:p-4 text-center border-t border-gray-100 mt-8">
            <p className="text-gray-600 text-xs sm:text-sm px-2">
              Schedule subject to minor adjustments. All sessions will be held at the Ethiopian Skylight Hotel Conference Center.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Schedule;